/**
 * adminNavTree · v2.7.37 — Re-ordered primary nav (Feb-2026 finance focus)
 * ───────────────────────────────────────────────────────
 *  Per Feb-2026 spec rewrite (v2.7.37):
 *    Contracts + Earnings now sit immediately after Dispatch and
 *    before Weather Commander — bringing the financial control surface
 *    to the top of the sidebar for daily operator visibility.
 *
 *      1. Dispatch
 *      2. Contracts (green accent · enterprise contracts + ledger)
 *      3. Earnings  (pink accent  · driver earnings + payouts)
 *      4. Weather Commander            (parent w/ children below)
 *           · Dashboard Overview
 *           · Live Weather Radar
 *           · NWS Discussions
 *               └ OUN Area Forecast Discussion
 *           · Active Warnings
 *           · Dispatch Warning
 *           · State Emergencies
 *      5. TAXIcab Commander
 *      —————————————————————————————————————————————————
 *      6. User Mgmt
 *      7. Fleet
 *      8. Accounting
 *      9. Developer
 *
 * Returns an array of top-level items, each of which may have `children[]`.
 */
import {
  Bell, Map, Users, Fuel, AlertTriangle, Lock,
  Calculator, Sparkles, DollarSign, CreditCard, Webhook, FileText, LifeBuoy, ScrollText,
  Zap, Plus, Receipt, Globe, Search,
  Palette, MessageCircle, ShieldCheck, Activity,
  Crown, Code2, Headphones, Car, Radar, User as UserIcon,
  Cloud, Radio, ShieldAlert, Send, BookOpen, MonitorPlay,
  Briefcase, Building2, Landmark, FlagTriangleRight, Truck,
  Mail, Inbox, Info, Settings, GraduationCap, Library,
  Clock, CalendarDays, CalendarClock, KeyRound, Ticket, Contact,
} from "lucide-react";
import { DropboxGlyph } from "@/components/brands/DropboxBrand";

export function buildAdminNavTree(adminRole, subsEnabled, adminRoles = [], badges = {}) {
  const rs = new Set([adminRole, ...(adminRoles || [])].filter(Boolean));
  const isDeveloper = rs.has("developer");
  const isOperatingPartner = rs.has("operating_partner") || rs.has("supervisor");
  // v2.7.58+E42 · General Partner is the SUPREME role — bypasses every
  // per-module gate and sees every entry regardless of what other flags
  // fire. Kept as its own boolean so future modules can differentiate
  // if they want (e.g. "God-mode only" surfaces).
  const isGeneralPartner = rs.has("general_partner");
  const isSuper = isDeveloper || isOperatingPartner || rs.has("admin") || isGeneralPartner;
  const isFieldSupervisor = rs.has("field_supervisor");
  const isTeamLeader = rs.has("team_leader");
  const isDispatcher = adminRole === "dispatcher";
  const isPrivileged = isSuper || isFieldSupervisor || isTeamLeader;
  const canSeeBranding = !isDispatcher;

  const items = [];

  // ════════════════════════════════════════════════════════════
  // PRIMARY NAV (positions 1-5) — these MUST appear first.
  // v2.7.37 · Contracts + Earnings hoisted to slots 2 & 3
  //           so finance operations sit between Dispatch and Weather.
  // ════════════════════════════════════════════════════════════

  // ── 1. Dispatch ──
  const dispatchChildren = [
    { sub: "live-map",       label: "Live Map",        Icon: Map,           accent: "sky",   targetTab: "live",         targetSub: null },
    ...(isPrivileged || isDispatcher ? [
      { sub: "coordination", label: "Ride Coordinator", Icon: Zap,          targetTab: "driver-mgmt", targetSub: "coordination" },
      { sub: "coordinator-search", label: "Coordinator Search", Icon: Search, targetTab: "driver-mgmt", targetSub: "coordinator-search" },
      { sub: "pending",      label: "Pending Rides",    Icon: AlertTriangle, targetTab: "driver-mgmt", targetSub: "pending" },
    ] : []),
    { sub: "book-ride",      label: "Dispatch Booking",       Icon: Plus,          targetTab: "driver-mgmt", targetSub: "book-ride" },
    ...(isPrivileged || isDispatcher ? [
      { sub: "rider-hours",  label: "Rider Portal Hours", Icon: Clock,         accent: "sky", href: "/admin/rider-hours" },
    ] : []),
    { sub: "chat",           label: "Live Chat",        Icon: MessageCircle, accent: "emerald", href: "/admin/chat" },
    { sub: "helpdesk",       label: "Helpdesk",         Icon: LifeBuoy,      accent: "emerald", href: "/admin/helpdesk" },
    { sub: "rides",          label: "Completed Rides",  Icon: Receipt,       targetTab: "driver-mgmt", targetSub: "rides" },
  ];
  items.push({
    value: "overview", label: "Dispatch", Icon: Bell, badge: "active-trips",
    children: dispatchChildren,
  });

  // ── 1b. Delivery (v2.7.65+E51) ──
  // Standalone parent section directly below Dispatch. The Dispatch
  // Queue (formerly "Delivery Queue" nested under Dispatch) moved here,
  // joined by the Delivery Payout Ledger. Live badge is populated by
  // /api/deliveries/admin/queue-count polled every 15s.
  if (isPrivileged || isDispatcher) {
    items.push({
      value: "delivery", label: "Delivery", Icon: Truck, accent: "amber",
      children: [
        { sub: "delivery-queue", label: "Dispatch Queue", Icon: Truck, accent: "amber",
           targetTab: "delivery-platform", targetSub: "queue", unreadCount: badges.deliveryQueue },
        { sub: "delivery-payout-ledger", label: "Delivery Payout Ledger", Icon: DollarSign, accent: "amber",
           targetTab: "delivery-platform", targetSub: "payouts" },
      ],
    });
  }

  // ── 2. Contracts (v2.7.35 · moved up to slot 2 in v2.7.37) ──
  // Enterprise contracts + financial ledger. Green accent per the
  // finance-focus visual hierarchy.
  const canSeeContracts = isSuper || isFieldSupervisor || isTeamLeader || isDispatcher;
  if (canSeeContracts) {
    items.push({
      value: "contracts", label: "Contracts", Icon: Briefcase, accent: "green",
      children: [
        { sub: "contracts-new",        label: "New Contract",        Icon: Plus,              href: "/admin/contracts/new", accent: "green" },
        { sub: "contracts-customer",   label: "Customer Contracts",  Icon: UserIcon,          href: "/admin/contracts/customer" },
        { sub: "contracts-business",   label: "Business Contracts",  Icon: Building2,         href: "/admin/contracts/business" },
        { sub: "contracts-municipal",  label: "Municipal Contracts", Icon: Landmark,          href: "/admin/contracts/municipal" },
        { sub: "contracts-state",      label: "State Contracts",     Icon: FlagTriangleRight, href: "/admin/contracts/state" },
        { sub: "contracts-federal",    label: "Federal Contracts",   Icon: Crown,             href: "/admin/contracts/federal" },
        { sub: "contracts-dot",        label: "DOT Contracts",       Icon: Truck,             href: "/admin/contracts/dot" },
      ],
    });
  }

  // ── 3. Earnings (hoisted to slot 3 in v2.7.37 · pink accent) ──
  if (!isDispatcher) {
    items.push({ value: "earnings", label: "Earnings", Icon: DollarSign, accent: "pink" });
  }

  // ── 4. Weather Commander (v2.7.33 · "OUN AFD" renamed to "NWS Forecast Discussions" + Active Power Outages added) ──
  //     The child for NWS Forecast Discussions is a deep-link into the
  //     existing Weather Commander page with #sub=nws-discussions — the
  //     structured tab extracts City + County names + WATCH (yellow) /
  //     WARNING (red) badges. Live numeric badges (from /admin/weather/badges)
  //     decorate Active Warnings + State Emergencies + the new discussions
  //     entry + Active Power Outages.
  items.push({
    value: "weather-commander", label: "Radar & Weather", Icon: Cloud, accent: "sky",
    children: [
      { sub: "weather-overview",     label: "Dashboard Overview",       Icon: Activity,       href: "/admin/weather#sub=overview" },
      { sub: "weather-radar",        label: "Live Weather Radar",       Icon: Radio,          href: "/admin/weather#sub=radar", accent: "sky" },
      { sub: "weather-extended",     label: "8-Day · 12-Day Forecast",  Icon: BookOpen,       href: "/admin/weather#sub=extended" },
      { sub: "weather-discussions",  label: "NWS Forecast Discussions", Icon: BookOpen,       href: "/admin/weather#sub=nws-discussions", unreadCount: badges.discussions },
      { sub: "weather-warnings",     label: "Active Warnings",          Icon: AlertTriangle,  href: "/admin/weather#sub=warnings",       unreadCount: badges.warnings },
      { sub: "weather-dispatch",     label: "Dispatch Warning",         Icon: Send,           href: "/admin/weather#sub=dispatch" },
      { sub: "weather-state-emerg",  label: "State Emergencies",        Icon: ShieldAlert,    href: "/admin/state-emergencies", accent: "fuchsia", unreadCount: badges.state_emergencies },
      { sub: "weather-outages",      label: "Active Power Outages",     Icon: Zap,            href: "/admin/weather/outages",   unreadCount: badges.outages_state_total },
      { sub: "weather-cms",          label: "Local Weather CMS",        Icon: Palette,        href: "/admin/weather/cms" },
    ],
  });

  // ── 5. Incident Reports ──
  items.push({ value: "commander", label: "Incident Reports", Icon: AlertTriangle, accent: "red" });

  // ════════════════════════════════════════════════════════════
  // SECONDARY NAV — everything else, in any order.
  // ════════════════════════════════════════════════════════════

  // ── 5. User Management ──
  items.push({ value: "user-mgmt", label: "User Mgmt", Icon: Users, section: "Taxi Commander Admin", children: [
    { sub: "users-riders", label: "Riders", Icon: UserIcon, targetTab: "user-mgmt" },
    { sub: "users-drivers", label: "Regular Drivers", Icon: Car, targetTab: "user-mgmt" },
    { sub: "users-team-leaders", label: "Team Leaders", Icon: ShieldCheck, targetTab: "user-mgmt" },
    { sub: "users-field-supervisors", label: "Field Supervisors", Icon: Radar, targetTab: "user-mgmt" },
    { sub: "users-dispatchers", label: "Dispatchers", Icon: Headphones, targetTab: "user-mgmt" },
    { sub: "users-general-partners", label: "General Partners", Icon: Crown, targetTab: "user-mgmt" },
    ...(isDeveloper ? [{ sub: "users-developers", label: "Developer", Icon: Code2, targetTab: "user-mgmt" }] : []),
    ...(isSuper ? [{ sub: "users-admins", label: "Admin", Icon: Lock, targetTab: "user-mgmt" }] : []),
    { sub: "users-applications", label: "Applications", Icon: Inbox, targetTab: "user-mgmt" }, // v2.7.65+E77 · Public "Drive With Us" submissions
    { sub: "users-contractor-setups", label: "Contractor Packets", Icon: FileText, targetTab: "user-mgmt" }, // v2.7.65+E135 · All packets incl. orphans
    { sub: "users-consent-audit", label: "Consent Audit", Icon: MessageCircle, targetTab: "user-mgmt" }, // v2.7.65+E168 · SMS opt-in audit
    ...(isSuper ? [{ sub: "dialpad-manager", label: "Dialpad Manager", Icon: Headphones, accent: "amber", href: "/admin/dialpad-manager" }] : []), // v2.8+E338
  ]});

  // ── 6. Fleet ── (v2.7.33 · Driver Safety nested for compliance grounding)
  const fleetChildren = [
    { sub: "fleet-overview", label: "Fleet Management", Icon: Fuel, targetTab: "fleet", targetSub: null },
    { sub: "driver-safety",  label: "Driver Safety",    Icon: ShieldCheck, href: "/admin/fleet/driver-safety", accent: "red" },
    // v2.8+E314 · DOT Compliance System disabled — "HOS Audit" +
    // "DOT Compliance" (Driver Time Control) nav entries hidden. DB retained.
  ];
  if (!isDispatcher) {
    fleetChildren.push({ sub: "metrics", label: "Metrics", Icon: Activity, targetTab: "metrics", targetSub: null });
  }
  items.push({ value: "fleet", label: "Fleet Management", Icon: Fuel, children: fleetChildren });

  // ── 7. Accounting (admin-only) ──
  if (!isDispatcher) {
    items.push({ value: "accounting", label: "Accounting", Icon: Calculator, children: [
      ...(subsEnabled ? [{ sub: "subscriptions", label: "Subscriptions", Icon: Sparkles, targetTab: "admin" }] : []),
      { sub: "pricing", label: "Pricing", Icon: DollarSign, targetTab: "admin" },
      { sub: "payments", label: "Payments", Icon: CreditCard, targetTab: "admin" },
      ...(isSuper ? [{ sub: "accounting", label: "Accounting (Audit)", Icon: Calculator, targetTab: "admin" }] : []),
    ] });
  }

  // ── 8. Developer (also hosts moved-Branding sub-section) ──
  if (isDeveloper || canSeeBranding) {
    const developerChildren = [];
    if (isDeveloper) {
      developerChildren.push(
        { sub: "devlicense",         label: "Dev License",        Icon: ScrollText, href: "/dev-license" },
        { sub: "system-health",      label: "System Health",      Icon: Activity,   href: "/admin/system-health" },
        // v2.8+E340 · Dispatch Video Wall HIDDEN until further notice (kept, not deleted).
        // { sub: "dispatch-video-wall", label: "Dispatch Video Wall", Icon: MonitorPlay, href: "/admin/developer/dispatch-video-wall", accent: "indigo" },
        { sub: "console-sessions",   label: "Console Sessions",    Icon: Activity,   href: "/admin/console-sessions", accent: "emerald" },
        { sub: "developer-portal",   label: "Developer Portal",   Icon: Globe,      targetTab: "admin" },
        { sub: "developer-coding",   label: "Developer Coding",   Icon: FileText,   targetTab: "admin" },
        { sub: "developer-branding", label: "Developer Branding", Icon: Palette,    targetTab: "admin" },
        // v2.8+E340 · NWS Palette Debug HIDDEN until further notice (kept, not deleted).
        // { sub: "palette-debug",      label: "NWS Palette Debug",  Icon: Palette,    href: "/admin/developer/palette-debug", accent: "fuchsia" },
        { sub: "api-keys",           label: "API Keys",           Icon: Webhook,    targetTab: "admin" },
        { sub: "audio-test",         label: "Audio Test Panel",   Icon: Bell,       targetTab: "admin" },
        { sub: "role-pins",          label: "Role PINs",          Icon: ShieldCheck, targetTab: "admin" },
      );
    }
    if (canSeeBranding) {
      developerChildren.push(
        { sub: "front-end",     label: "Front End",          Icon: Globe,    targetTab: "admin" },
        { sub: "integrations",  label: "API & Integrations", Icon: Webhook,  targetTab: "admin" },
        { sub: "legal",         label: "Legal",              Icon: FileText, targetTab: "admin" },
        { sub: "support",       label: "Support",            Icon: LifeBuoy, targetTab: "admin" },
      );
    }
    items.push({
      value: "developer", label: "Developer", Icon: Lock, accent: "indigo",
      children: developerChildren,
    });
  }

  return items;
}

// Navigation helper — unchanged from v2.7.18.
export function navigateAdmin(onTopNav, { targetTab, targetSub, value }) {
  const tab = targetTab || value;
  onTopNav(tab);
  if (targetSub === null) {
    const h = new URLSearchParams((window.location.hash || "").replace(/^#/, ""));
    if (h.has("sub")) {
      h.delete("sub");
      const next = h.toString();
      window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}${next ? `#${next}` : ""}`);
      window.dispatchEvent(new HashChangeEvent("hashchange"));
    }
    return;
  }
  if (targetSub) {
    const h = new URLSearchParams((window.location.hash || "").replace(/^#/, ""));
    h.set("sub", targetSub);
    window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}#${h.toString()}`);
    window.dispatchEvent(new HashChangeEvent("hashchange"));
  }
}


// ════════════════════════════════════════════════════════════════════
// v2.7.41 · buildAdminMenus — 3-menu layout (Admin Console / Accounting
// / App Management) per Feb-2026 sidebar restoration spec. Items inside
// each menu are FLAT (hoisted, no dropdowns). Returns the new structure
// directly; `buildAdminNavTree` above stays unchanged for callers that
// still use the legacy flat top-bar layout.
//
// Shape:
//   [
//     { id, title, headerMeta?: true,
//       items: [{ key, label, Icon, accent?, href?, targetTab?, targetSub?,
//                  isHeading?: true, badge?: string }] }, ...
//   ]
// ════════════════════════════════════════════════════════════════════
export function buildAdminMenus(
  adminRole,
  subsEnabled,
  adminRoles = [],
  badges = {},
  emailAccounts = [],
  emailUnread = {},
  isMasterOwner = false,        // v2.7.50+E26 Phase 1
) {
  const rs = new Set([adminRole, ...(adminRoles || [])].filter(Boolean));
  const isDeveloper = rs.has("developer");
  const isOperatingPartner = rs.has("operating_partner") || rs.has("supervisor");
  // v2.7.58+E42 · General Partner supreme-user parity in the second nav.
  const isGeneralPartner = rs.has("general_partner");
  const isSuper = isDeveloper || isOperatingPartner || rs.has("admin") || isGeneralPartner;
  const isFieldSupervisor = rs.has("field_supervisor");
  const isTeamLeader = rs.has("team_leader");
  const isDispatcher = adminRole === "dispatcher";
  const isPrivileged = isSuper || isFieldSupervisor || isTeamLeader;
  const canSeeContracts = isSuper || isFieldSupervisor || isTeamLeader || isDispatcher;
  const canSeeBranding = !isDispatcher;

  // ─────────────────────── Menu 1 · Admin Console ───────────────────────
  // v2.7.65+E201 · Admin Console Nav Restructure (2026-02-27):
  //   • DISPATCH heading + Live Map + Ride Coordinator + Pending Rides
  //     + Dispatch Booking + Live Chat + Helpdesk + Completed Rides
  //     REMOVED from menu1. Everything except Live Map is re-hosted in
  //     the new "Trip Manager" section below (menuTripManager).
  //   • RADAR & WEATHER row hidden.
  //   • DRIVER APP MANAGER row hidden.
  // Rows explicitly untouched: Contracts, Incident Reports, Fleet
  // Management, App Management Portal, Voucher Manager, DOT Compliance,
  // Account Users, Master Access.
  const menu1 = [
    ...(canSeeContracts ? [
      { key: "contracts",     label: "CONTRACTS",         Icon: Briefcase,      accent: "green",   href: "/admin/contracts/state" },
    ] : []),
    { key: "taxicab-commander", label: "INCIDENT REPORTS", Icon: AlertTriangle, accent: "red",    targetTab: "commander" },
    { key: "taxicab-fleet",   label: "FLEET MANAGEMENT",     Icon: Fuel,           targetTab: "fleet" },
    // v2.7.65+E128 · App Management Portal remains under ADMIN CONSOLE.
    // Portal: super_user + general_partner + dispatch (read).
    ...((isSuper || isGeneralPartner || isDispatcher) ? [
      { key: "app-management-portal", label: "APP MANAGEMENT PORTAL",
        Icon: ShieldCheck, accent: "amber",
        href: "/admin/app-management-portal" },
    ] : []),

    // v2.7.65+E137 · CCVAP Voucher Manager. Visible to admin, super_user,
    // general_partner, dispatch, and field_supervisor per user directive.
    // Backend already role-gates each sub-endpoint: L5 admins get the full
    // approval workflow; dispatchers get the read-only Serial Verification
    // lookup only (the VoucherAdminPage handles this branching client-side).
    ...((isSuper || isGeneralPartner || isDispatcher || isPrivileged) ? [
      { key: "voucher-manager", label: "VOUCHER MANAGER (CCVAP)",
        Icon: Ticket, accent: "amber",
        href: "/admin/vouchers" },
    ] : []),
    // v2.8+E314 · DOT COMPLIANCE (Driver Time Control) sidebar row removed —
    // DOT Compliance System disabled platform-wide. Backend data retained.
    { key: "account-users",   label: "ACCOUNT USERS",     Icon: Users,          targetTab: "user-mgmt" },
    // v2.8+E351 · Team Photo Wall — every Admin Console user.
    { key: "team-wall",       label: "TEAM PHOTO WALL",   Icon: Contact,        accent: "amber", targetTab: "team-wall" },
    // v2.8+E338 · Dialpad Manager — sits directly under Account Users.
    // Super Users + Admins only (backend also enforces via _is_full).
    ...(isSuper ? [
      { key: "dialpad-manager", label: "DIALPAD MANAGER", Icon: Headphones, accent: "amber", href: "/admin/dialpad-manager" },
    ] : []),
    // v2.7.50+E26 Phase 1 · Master Access entry visible ONLY to the
    // master env-admin (the primary system owner). Sits below Account
    // Users since it's a peer concept (user privilege management) and
    // uses the amber accent to signal owner-only weight.
    ...(isMasterOwner ? [
      { key: "master-access", label: "MASTER ACCESS",     Icon: Crown,          accent: "amber",   href: "/admin/master-access" },
    ] : []),
    // v2.7.51+E27 Storage Portal Phase 2 · Dropbox - Business now lives
    // inside the dedicated "Corporate File Sharing" accordion below.
  ];

  // ─────────────────────── Menu 1½ · Trip Manager (v2.7.65+E201) ────────
  // New section that consolidates the operational ride items that used
  // to live inside the DISPATCH heading + the external DispatchBar. All
  // items deep-link to their existing pages — no new routes created.
  // Live Map is intentionally excluded per operator directive.
  // v2.7.65+E207 · Trip Manager item order (user directive Feb-2026):
  //   +Dispatch Booking → Ride Coordinator → Pending Rides → Live Chat →
  //   Helpdesk → Completed Rides. Ride Coordinator/Pending Rides retain
  //   their (isPrivileged || isDispatcher) gate; only their sequence
  //   moved. No page logic, links, or functionality changed.
  const menuTripManager = [
    { key: "book-ride",       label: "Dispatcher", Icon: Plus,           targetTab: "driver-mgmt", targetSub: "book-ride" },
    ...(isPrivileged || isDispatcher ? [
      { key: "coordination",  label: "Ride Coordinator",  Icon: Zap,            targetTab: "driver-mgmt", targetSub: "coordination" },
      { key: "coordinator-search", label: "Coordinator Search", Icon: Search,   targetTab: "driver-mgmt", targetSub: "coordinator-search" },
      { key: "future-fares",  label: "Future Fares",      Icon: CalendarClock,  accent: "violet", href: "/admin/future-fares" },
      { key: "pending",       label: "Pending Rides",     Icon: AlertTriangle,  targetTab: "driver-mgmt", targetSub: "pending" },
      // v2.8+E342 · Dispatcher Schedule — immediately after Pending Rides.
      { key: "schedule",      label: "Schedule",          Icon: CalendarClock,  accent: "violet", href: "/admin/schedule" },
    ] : []),
    { key: "chat",            label: "Live Chat",         Icon: MessageCircle,  accent: "emerald", href: "/admin/chat" },
    { key: "helpdesk",        label: "Helpdesk",          Icon: LifeBuoy,       accent: "emerald", href: "/admin/helpdesk" },
    { key: "rides",           label: "Completed Rides",   Icon: Receipt,        targetTab: "driver-mgmt", targetSub: "rides" },
  ];

  // ─────────────────────── Menu 1b · Delivery (v2.7.65+E51) ───────────────────────
  // Standalone parent section directly below "Admin Console". The
  // Dispatch Queue (historically the "Delivery Queue" item nested in
  // Menu 1) moved here, joined by the Delivery Payout Ledger.
  const menuDelivery = (isPrivileged || isDispatcher) ? [
    { key: "delivery-queue", label: "Dispatch Queue", Icon: Truck, accent: "amber",
       targetTab: "delivery-platform", targetSub: "queue", unreadCount: badges.deliveryQueue },
    { key: "delivery-payout-ledger", label: "Delivery Payout Ledger", Icon: DollarSign, accent: "amber",
       targetTab: "delivery-platform", targetSub: "payouts" },
  ] : [];

  // ─────────────────────── Menu 2 · Accounting ───────────────────────
  const menu2 = !isDispatcher ? [
    { key: "accounting",      label: "ACCOUNTING",        Icon: Calculator,     targetTab: "admin", targetSub: "accounting" },
    { key: "earnings",        label: "EARNINGS",          Icon: DollarSign,     accent: "pink", targetTab: "earnings" },
    { key: "payments",        label: "PAYMENTS",          Icon: CreditCard,     targetTab: "admin", targetSub: "payments" },
    { key: "rider-prices",    label: "RIDER PRICES",      Icon: Receipt,        targetTab: "admin", targetSub: "rider-price-page" },
    { key: "prices",          label: "PRICES",            Icon: DollarSign,     targetTab: "admin", targetSub: "pricing" },
    // v2.8+E342 · Time Sheets — Field Supervisor and above (menu2 is already
    // !isDispatcher; backend also enforces mgmt tier per endpoint).
    { key: "time-sheets",     label: "TIME SHEETS",       Icon: Clock,          accent: "violet", href: "/admin/time-sheets" },
    // v2.7.57+E34 · DELIVERY SYSTEM · Section 2 · pricing engine + payment
    // hookup. Sits directly under PRICES in the Accounting group since the
    // 6 tunables ARE pricing. Amber accent unifies it visually with the
    // marketing-site DELIVERY link. Section 3 will add child sub-items
    // (Dispatch Queue · Document Center · Driver Assignments).
    { key: "delivery-platform", label: "DELIVERY PLATFORM", Icon: Truck, accent: "amber", targetTab: "delivery-platform" },
    ...(subsEnabled ? [
      { key: "subscriptions", label: "SUBSCRIPTIONS",     Icon: Sparkles,       targetTab: "admin", targetSub: "subscriptions" },
    ] : []),
  ] : [];

  // ─────────────────────── Menu 3 · App Management ───────────────────────
  const menu3 = [];
  // v2.7.65+E128 · (App Management Portal moved into Menu 1 · Admin Console)
  // v2.7.45 · Phase-1 access fix · The Dispatch Video Wall + supporting
  // dev tools were previously gated behind `isDeveloper` only, which hid
  // the link entirely from admins/supervisors who legitimately needed to
  // configure the kiosk grid. Open the section to any super_user
  // (admin/supervisor/operating_partner/developer) so the kiosk surface
  // is always reachable from the sidebar.
  const canSeeDevTools = isDeveloper || isSuper;
  if (canSeeDevTools) {
    menu3.push(
      { key: "developer",            label: "DEVELOPER",            Icon: Lock,        accent: "indigo", isHeading: true, targetTab: "admin" },
      { key: "dispatch-video-wall",  label: "DISPATCH VIDEO WALL",  Icon: MonitorPlay, accent: "indigo", href: "/admin/developer/dispatch-video-wall" },
      { key: "developer-portal",     label: "DEVELOPER PORTAL",     Icon: Globe,       targetTab: "admin", targetSub: "developer-portal" },
      { key: "developer-branding",   label: "DEVELOPER BRANDING",   Icon: Palette,     targetTab: "admin", targetSub: "developer-branding" },
      { key: "palette-debug",        label: "NWS PALETTE DEBUG",    Icon: Palette,     accent: "fuchsia", href: "/admin/developer/palette-debug" },
      { key: "api-keys",             label: "API KEYS",             Icon: Webhook,     targetTab: "admin", targetSub: "api-keys" },
      { key: "audio-test",           label: "AUDIO TEST PANEL",     Icon: Bell,        targetTab: "admin", targetSub: "audio-test" },
      { key: "role-pins",            label: "ROLE PINS",            Icon: ShieldCheck, targetTab: "admin", targetSub: "role-pins" },
    );
  }
  if (canSeeBranding) {
    menu3.push(
      { key: "website-management",   label: "WEBSITE MANAGEMENT",   Icon: Globe,       targetTab: "admin", targetSub: "front-end" },
      { key: "api-integrations",     label: "API & INTEGRATIONS",   Icon: Webhook,     targetTab: "admin", targetSub: "integrations" },
      { key: "tex-notification-manager", label: "TEXT NOTIFICATION MANAGER", Icon: Send, accent: "amber", targetTab: "admin", targetSub: "tex-notify" },
      { key: "api-vault",            label: "API VAULT · LEVEL 0",  Icon: KeyRound,     accent: "rose", href: "/admin/api-vault" },
      { key: "legal",                label: "LEGAL",                Icon: FileText,    targetTab: "admin", targetSub: "legal" },
      { key: "support",              label: "SUPPORT",              Icon: LifeBuoy,    targetTab: "admin", targetSub: "support" },
    );
  }

  // ─────────────────────── Menu 3½ · Calendar (own section) ──────────────
  // v2.7.65+E104 · Calendar promoted from a single row inside App Management
  // into its own top-level section, sitting directly above Corporate Email
  // so daily scheduling reads before the mailbox stack. Single-item menu for
  // now; recurring shifts + upcoming Google Sync will live under this header.
  const menuCalendar = [
    { key: "calendar", label: "CALENDAR & TASK SCHEDULER", Icon: CalendarDays, accent: "blue", href: "/admin/calendar" },
  ];

  // ─────────────────────── Menu 4 · Corporate Email ───────────────────────
  // v2.7.45+E11 (Feb-2026) — UI cleanup:
  //   • The redundant "CORPORATE EMAIL" heading row was removed because
  //     the section title at the top of the menu already labels the
  //     block. Showing both was visually noisy.
  //   • Each alias mailbox now gets a distinct Lucide icon + accent
  //     color so a glance differentiates dispatch/support/trips/legal
  //     at the row level (rather than identical Inbox+sky for all).
  //   • Parent mailboxes (derick@, steven@) keep neutral inbox/user
  //     icons so they remain clearly distinct from the aliases.
  //
  //   Mapping rules (by email local-part):
  //     derick  → Inbox    · amber   (primary mailbox owner)
  //     dispatch→ Radio    · sky     (real-time radio comms)
  //     support → Headphones · emerald (customer service)
  //     info    → Info     · amber   (general inbound)
  //     trips   → Truck    · green   (operations / fleet)
  //     billing → CreditCard · pink   (finance / payments)
  //     legal   → ShieldAlert · rose  (legal counsel)
  //     steven  → UserIcon · indigo  (executive mailbox)
  //     <other> → fallback Inbox/Send · sky
  const EMAIL_ICON_MAP = {
    derick:   { Icon: Inbox,       accent: "amber"   },
    dispatch: { Icon: Radio,       accent: "sky"     },
    support:  { Icon: Headphones,  accent: "emerald" },
    info:     { Icon: Info,        accent: "amber"   },
    trips:    { Icon: Truck,       accent: "green"   },
    billing:  { Icon: CreditCard,  accent: "pink"    },
    legal:    { Icon: ShieldAlert, accent: "rose"    },
    steven:   { Icon: UserIcon,    accent: "indigo"  },
  };
  const resolveEmailVisuals = (addr, isAlias) => {
    const local = String(addr || "").split("@")[0].toLowerCase();
    if (EMAIL_ICON_MAP[local]) return EMAIL_ICON_MAP[local];
    return { Icon: isAlias ? Send : Inbox, accent: "sky" };
  };

  const accountRows = (emailAccounts || []).map((a) => {
    const count = (emailUnread && typeof emailUnread[a.email] === "number") ? emailUnread[a.email] : 0;
    const vis = resolveEmailVisuals(a.email, a.is_alias);
    return {
      key:                `email-account-${a.id}`,
      label:              a.email,
      Icon:               vis.Icon,
      accent:             vis.accent,
      href:               `/admin/email?account=${encodeURIComponent(a.id)}`,
      unreadCount:        count,
      isEmailAlias:       !!a.is_alias,
      markReadAccountId:  a.id,
    };
  });
  // No more "CORPORATE EMAIL" heading row — the section title already
  // labels this block. MAILBOXES + per-account aliases + Settings only.
  // v2.7.45+E15 · Email Admin link visible to canonical_role ∈ {developer,
  // admin, operating_partner} — Operating Partner now has executive parity
  // with Developer per the leadership-alignment update. Renamed from
  // "Developer Email Admin" to just "Email Admin".
  const showDevConsole = isDeveloper || rs.has("admin") || rs.has("operating_partner") || isGeneralPartner;
  const menu4 = [
    { key: "email-client",    label: "MAILBOXES",       Icon: Mail,     accent: "sky", href: "/admin/email" },
    ...accountRows,
    // v2.7.65+E138 · Manage Emails (GP-only) — provisioning + assignment hub.
    // Rendered for anyone; the page itself renders a friendly "GP only"
    // panel for non-privileged roles.
    { key: "manage-emails",   label: "MANAGE EMAILS",   Icon: Users,    accent: "amber", href: "/admin/email/manage" },
    { key: "email-settings",  label: "MAIL ACCOUNT SETTINGS", Icon: Settings, accent: "sky", href: "/admin/email?settings=1" },
    ...(showDevConsole ? [
      { key: "email-admin", label: "EMAIL ADMIN", Icon: Code2, accent: "violet", href: "/admin/developer-email" },
    ] : []),
  ];

  // ─────────────────────── Menu 5 · Corporate File Sharing ───────────────────────
  // v2.7.53+E29 · "Corporate File Sharing" accordion sits directly below
  // "Corporate Email". 3-tier RBAC matrix (matches backend):
  //   • Driver & below  → hidden completely (sidebar row not rendered)
  //   • Dispatcher / Staff (team_leader, dispatcher, field_supervisor)
  //                      → visible · workspace opens in READ-ONLY mode
  //                        (list, navigate, download, star — no upload/share/delete)
  //   • Developer / Partner / Owner (operating_partner, developer, admin),
  //     OR is_super_root flag, OR the master env-owner
  //                      → visible · full read/write incl. credential management
  // The Dropbox workspace itself enforces the read-only UI tier server-side
  // by checking the `access_level` field on /admin/dropbox/status; all write
  // endpoints return 403 to read-tier callers regardless of UI state.
  // v2.7.65+E138 · Dropbox integration is globally suppressed via
  // REACT_APP_FEATURE_DROPBOX_ENABLED=false. When the flag is off we skip
  // the entire menu section — no sidebar entry, no accordion header. The
  // `/admin/dropbox` route itself also redirects to /admin/dashboard (see
  // App.js) so bookmarks stop working.
  const dropboxEnabled = String(process.env.REACT_APP_FEATURE_DROPBOX_ENABLED || "").toLowerCase() === "true";
  const canSeeFileSharing = dropboxEnabled && (isMasterOwner || isSuper || isFieldSupervisor || isTeamLeader || isDispatcher);
  const menu5 = canSeeFileSharing ? [
    { key: "dropbox", label: "Dropbox - Business", Icon: DropboxGlyph, accent: "dropbox", href: "/admin/dropbox" },
  ] : [];

  // ─────────────────────── Menu 6 · Training Center ───────────────────────
  // v2.7.64+E49 · Standalone "Training Center" accordion directly BENEATH
  // "Corporate File Sharing" (File Sharing untouched — this is its own
  // independent root). Visible to every staff tier that can open the
  // console; the backend enforces read (staff) vs curate (supervisor+)
  // per endpoint, so no sidebar gating beyond staff membership is needed.
  const menu6 = [
    { key: "training-wiki", label: "Knowledge Center (WIKI)",           Icon: BookOpen,      accent: "amber",   href: "/admin/training-center/wiki" },
    { key: "training-lms",  label: "Learning Management Center (LMS)",  Icon: GraduationCap, accent: "emerald", href: "/admin/training-center/lms" },
    { key: "training-kmc",  label: "Knowledge Management Center (KMC)", Icon: Library,       accent: "violet",  href: "/admin/training-center/kmc" },
  ];

  // ════════════════════════════════════════════════════════════════════
  // v2.8+E340 · Full side-menu reorganization into labeled sections.
  // LAYOUT ONLY — items are regrouped by key from the pools built above;
  // every item keeps its exact href/targetTab/gating (gated-out items are
  // simply absent from the pool, so access permissions are unchanged).
  // Section order per the Jun-2026 operator directive.
  //   • OPERATIONS (Dispatch Video Wall + Calendar & Task Scheduler) —
  //     HIDDEN until further notice: their defs are retained in the pools
  //     above but NOT selected here, so nothing is deleted.
  //   • NWS Palette Debug — likewise retained but not selected (hidden).
  //   • Rider Prices merged into ONE "Prices" entry (the Rider Price Page
  //     remains an in-page tab on the Pricing screen — nothing lost).
  //   • Developer Tools — Super-User only + collapsed by default (shell).
  // ════════════════════════════════════════════════════════════════════
  const _pool = {};
  [...menu1, ...menuTripManager, ...menuDelivery, ...menu2, ...menu3, ...menuCalendar, ...menu5, ...menu6]
    .forEach((i) => { if (i && i.key) _pool[i.key] = i; });
  const pick = (...keys) => keys.map((k) => _pool[k]).filter(Boolean);

  return [
    { id: "operations",        title: "Operations",           items: pick(/* dispatch-video-wall + calendar — HIDDEN */) },
    { id: "ride-coordinator",  title: "Ride Coordinator",     items: pick("coordination", "coordinator-search", "future-fares", "pending", "schedule") },
    { id: "trip-manager",      title: "Trip Manager",         items: pick("book-ride", "chat", "helpdesk", "rides") },
    { id: "delivery",          title: "Delivery",             items: pick("delivery-queue", "delivery-platform", "delivery-payout-ledger") },
    { id: "accounting",        title: "Accounting & Finance", items: pick("accounting", "earnings", "payments", "subscriptions", "prices", "time-sheets") },
    { id: "fleet-staff",       title: "Fleet & Staff",        items: pick("taxicab-fleet", "account-users", "team-wall", "master-access", "contracts", "taxicab-commander", "voucher-manager") },
    { id: "integrations-apis", title: "Integrations & APIs",  items: pick("api-keys", "api-integrations", "api-vault", "tex-notification-manager", "dialpad-manager") },
    { id: "training-center",   title: "Training Center",      items: pick("training-wiki", "training-lms", "training-kmc") },
    { id: "system-settings",   title: "System Settings",      items: pick("audio-test", "role-pins", "website-management", "app-management-portal") },
    ...(isSuper ? [
      { id: "developer-tools", title: "Developer Tools",      items: pick("developer-portal", "developer-branding") },
    ] : []),
    { id: "admin-compliance",  title: "Admin & Compliance",   items: pick("legal", "support") },
    // Corporate File Sharing (Dropbox) retained — renders only when the
    // feature flag is on for a permitted role (unchanged).
    { id: "file-sharing",      title: "Corporate File Sharing", items: menu5 },
  ].filter((m) => m.items.length > 0);
}
